import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, FileText, Trash2, Upload, X } from "lucide-react";
import api, { BACKEND_URL, formatApiErrorDetail } from "../../lib/api";

const ACCEPT = "image/png,image/jpeg,image/gif,image/webp,image/svg+xml,image/avif,application/pdf,text/plain,text/markdown";

export function absoluteMediaUrl(item) {
  if (item.url && !/^https?:\/\/(localhost|127\.0\.0\.1)/.test(item.url)) return item.url;
  return `${BACKEND_URL}/api/media/${item.id}/file`;
}

export async function uploadMediaFiles(fileList) {
  const files = Array.from(fileList || []);
  if (!files.length) return [];
  const form = new FormData();
  files.forEach((file) => form.append("file", file));
  const { data } = await api.post("/media", form);
  return Array.isArray(data) ? data : [data];
}

function useMediaLibrary(active = true) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/media");
      setItems(data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load media");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  const upload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return [];
    setUploading(true);
    try {
      const created = await uploadMediaFiles(files);
      toast.success(`Uploaded ${created.length} file${created.length > 1 ? "s" : ""}.`);
      await load();
      return created;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
      return [];
    } finally {
      setUploading(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.filename}? Content using it will lose the image.`)) return;
    try {
      await api.delete(`/media/${item.id}`);
      toast.success("Media deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  const copy = async (item) => {
    const url = absoluteMediaUrl(item);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied to clipboard.");
    } catch (err) {
      toast.error("Copy failed - select the URL manually.");
    }
  };

  return { items, loading, uploading, dragging, setDragging, inputRef, load, upload, remove, copy };
}

function MediaTile({ item, onCopy, onDelete, onUse, testidPrefix }) {
  const url = absoluteMediaUrl(item);
  const isImage = (item.content_type || "").startsWith("image/");
  return (
    <div className="card-tech p-3 flex flex-col gap-2" data-testid={`${testidPrefix}-item-${item.id}`}>
      <div className="aspect-video bg-ink-950/60 border border-white/10 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img src={url} alt={item.filename} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <FileText className="w-8 h-8 text-fog-300" />
        )}
      </div>
      <p className="text-sm truncate" title={item.filename}>{item.filename}</p>
      <p className="text-xs text-fog-400">{(item.size / 1024).toFixed(0)} KB</p>
      <div className="flex items-center gap-2">
        {onUse && (
          <button type="button" className="btn-volt px-3 py-1 text-xs" onClick={() => onUse(item)} data-testid={`${testidPrefix}-use-${item.id}`}>
            Use
          </button>
        )}
        <button type="button" className="btn-ghost px-3 py-1 text-xs" onClick={() => onCopy(item)} data-testid={`${testidPrefix}-copy-${item.id}`}>
          <Copy className="w-3 h-3" />
        </button>
        <button
          type="button"
          className="btn-ghost px-3 py-1 text-xs text-red-300 border-red-400/40 hover:text-red-200"
          onClick={() => onDelete(item)}
          data-testid={`${testidPrefix}-delete-${item.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}

export default function MediaLibrary() {
  const { items, loading, uploading, dragging, setDragging, inputRef, upload, remove, copy } = useMediaLibrary();

  return (
    <div data-testid="admin-media-library">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display font-bold text-2xl">Media</h2>
        <button type="button" className="btn-volt" onClick={() => inputRef.current?.click()} data-testid="media-upload-btn">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload"}
        </button>
      </div>
      <p className="text-fog-300 text-sm mb-4">
        Upload images, PDFs or documents here, then copy the URL into the gallery, profile or blog editors.
        Files stay on the backend, so they survive redeploys.
      </p>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }}
        data-testid="media-upload-input"
      />

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files); }}
        className={`border-2 border-dashed p-8 text-center mb-8 transition ${
          dragging ? "border-volt bg-volt/10" : "border-white/15"
        }`}
        data-testid="media-dropzone"
      >
        <p className="text-fog-200">Drag files here, or use the Upload button.</p>
        <p className="text-fog-400 text-sm mt-1">PNG, JPG, GIF, WEBP, SVG, AVIF, PDF, TXT, MD</p>
      </div>

      {loading && <p className="text-fog-300">Loading…</p>}
      {!loading && items.length === 0 && <p className="text-fog-300">No media uploaded yet.</p>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <MediaTile key={item.id} item={item} onCopy={copy} onDelete={remove} testidPrefix="media" />
        ))}
      </div>
    </div>
  );
}

export function MediaPickerModal({ open, onClose, onSelect }) {
  const { items, loading, uploading, inputRef, upload, remove } = useMediaLibrary(open);

  if (!open) return null;

  const handleUpload = async (files) => {
    const created = await upload(files);
    if (created.length) onSelect(absoluteMediaUrl(created[created.length - 1]));
  };

  return (
    <div className="fixed inset-0 z-[60] bg-ink-950/85 backdrop-blur flex items-center justify-center p-4" data-testid="media-picker-modal">
      <div className="card-tech w-full max-w-4xl max-h-[85vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Media library</h3>
          <button type="button" className="text-fog-300 hover:text-white" onClick={onClose} data-testid="media-picker-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={ACCEPT}
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files); e.target.value = ""; }}
            data-testid="media-picker-upload-input"
          />
          <button type="button" className="btn-volt mb-5" onClick={() => inputRef.current?.click()} data-testid="media-picker-upload-btn">
            <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : "Upload new"}
          </button>

          {loading && <p className="text-fog-300">Loading…</p>}
          {!loading && items.length === 0 && (
            <p className="text-fog-300 text-sm">Nothing uploaded yet - upload a file to get its URL.</p>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <MediaTile
                key={item.id}
                item={item}
                onUse={(chosen) => { onSelect(absoluteMediaUrl(chosen)); onClose(); }}
                onDelete={remove}
                testidPrefix="media-picker"
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}